import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import AiMark, { isAiReason } from '../../components/ui/AiMark.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { Facts, HowThisPageWorks, Loading, Section } from '../../components/ui/index.jsx';
import { fmtDateTime } from '../../utils/format.js';
import { AnswerCard } from '../inbox/inboxShared.jsx';
import { RegisteredAgo, SOURCE, VERDICT, defangDomain } from './lookalikeShared.jsx';

const CHOICES = [
  ['scam', 'No, it’s a scam', 'We warn everyone who checks it, catch emails linking to it, and you can ask browsers to block it.'],
  ['ours', 'Yes, it’s ours', 'Ours, or our agency’s. We stop flagging it and just look at it once a month.'],
  ['watch', 'Not sure yet', 'We keep watching it and tell you if it starts copying you or taking payments.'],
  ['not_related', 'Nothing to do with us', 'Someone else’s genuine site that happens to have a similar name.'],
];
const STRONG = new Set(['threat_listed', 'urlscan_malicious', 'has_your_code', 'loads_your_files', 'same_tracking', 'same_text', 'asks_card', 'asks_login', 'form_elsewhere']);

function DecideModal({ site, choice, onClose, onDone, base }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [, label, what] = CHOICES.find((c) => c[0] === choice);
  const save = async () => {
    setBusy(true); setError('');
    try { await api(`${base}/decision`, { method: 'POST', body: { decision: choice, note } }); onDone(); } catch (err) { setError(err.message); setBusy(false); }
  };
  return (
    <Modal title={`${defangDomain(site.domain)}: ${label.toLowerCase()}?`} onClose={onClose} onSubmit={save} busy={busy} description={what}
      footer={<ModalActions submitLabel={label} busyLabel="Saving…" busy={busy} onCancel={onClose} danger={choice === 'scam'} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <Field as="textarea" label="Note" optional rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder={choice === 'scam' ? 'Reported to the registrar on…' : ''} />
    </Modal>
  );
}

const fmtDay = (v) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null);

/** A takedown request for the registrar's abuse desk, written from what we found. */
function abuseMail(site, organisationName) {
  const facts = site.reasons.filter((r) => r.points > 0 || ['asks_card', 'asks_login', 'form_elsewhere'].includes(r.code)).map((r) => `- ${r.text}`);
  const body = [
    'Hello,',
    '',
    `The domain ${site.domain} is being used to impersonate ${organisationName}${site.final_url ? ` (${site.final_url})` : ''}.`,
    '',
    ...(facts.length ? ['What we found on it:', ...facts, ''] : []),
    site.ip ? `It is hosted at ${site.ip}.` : null,
    `We first saw it on ${fmtDay(site.created_at)}. Screenshots and the page as we found it are available on request.`,
    '',
    `Please suspend the domain under your abuse policy. ${organisationName} has no connection with it.`,
    '',
    'Thank you,',
    organisationName,
  ].filter((l) => l !== null).join('\n');
  return `mailto:${site.rdap.abuse.email}?subject=${encodeURIComponent(`Abuse report: ${site.domain} impersonating ${organisationName}`)}&body=${encodeURIComponent(body)}`;
}

/** Registration data (RDAP — what lookup.icann.org shows): registrar, dates, abuse contact. */
function Registration({ site, organisationName }) {
  const r = site.rdap;
  const icann = `https://lookup.icann.org/en/lookup?name=${encodeURIComponent(site.domain)}`;
  const desc = <>From the domain&rsquo;s registry (RDAP, the same data as <a href={icann} target="_blank" rel="noreferrer">ICANN Lookup</a>){site.rdap_checked_at ? `, ${fmtDay(site.rdap_checked_at)}` : ''}. Refreshed weekly.</>;
  if (!r) return <Section title="Registration" description={desc}><p className="muted">{site.rdap_checked_at ? 'No registration data for this site (it’s on a shared host, such as a site builder’s).' : 'Not looked up yet — it is on the next check.'}</p></Section>;
  if (r.not_registered) return <Section title="Registration" description={desc}><p className="muted">The registry says this domain isn’t registered (any more).</p></Section>;
  if (r.unavailable) return <Section title="Registration" description={desc}><p className="muted">{r.unavailable} <a href={icann} target="_blank" rel="noreferrer">Try ICANN Lookup</a>.</p></Section>;
  const registrant = r.registrant && (r.registrant.name || r.registrant.org)
    ? [r.registrant.name, r.registrant.org, r.registrant.country].filter(Boolean).join(', ')
    : <span className="muted">Hidden for privacy{r.registrant?.country ? ` · country: ${r.registrant.country}` : ''}</span>;
  return (
    <Section title="Registration" description={desc}
      actions={r.abuse?.email && <a className="btn btn-secondary btn-small" href={abuseMail(site, organisationName)}>Report to the registrar</a>}>
      <Facts items={[
        ['Registered', r.registered_at ? <RegisteredAgo at={r.registered_at} /> : '—'],
        ['Registrar', r.registrar ? <>{r.registrar.url ? <a href={r.registrar.url} target="_blank" rel="noreferrer">{r.registrar.name}</a> : r.registrar.name}{r.registrar.iana_id && <span className="muted small"> · IANA ID {r.registrar.iana_id}</span>}</> : '—'],
        ['Abuse contact', r.abuse ? <>{r.abuse.email && <a href={`mailto:${r.abuse.email}`}>{r.abuse.email}</a>}{r.abuse.phone && <span className="muted"> · {r.abuse.phone}</span>}</> : '—'],
        ['Registrant', registrant],
        ['Expires', fmtDay(r.expires_at) || '—'],
        ['Last changed', fmtDay(r.updated_at) || '—'],
        r.nameservers?.length > 0 && ['Name servers', <span className="mono small">{r.nameservers.join(', ')}</span>],
        r.status?.length > 0 && ['Status', <span className="small">{r.status.join(', ')}</span>],
      ]} />
      {r.abuse?.email && <p className="muted small">“Report to the registrar” opens an email to their abuse desk, written from what we found, for you to check and send. Registrars act fastest when the report says exactly what the site does.</p>}
    </Section>
  );
}

/** A listing on an event platform: what it says, who runs it, and how to report it there. */
function ListingFacts({ site }) {
  const l = site.listing;
  return (
    <Section title={`The ${l.platform} listing`} description="From the listing’s own event data. The platform itself isn’t flagged — only this listing.">
      <Facts items={[
        ['Listing', <span className="mono break">{defangDomain(site.page_url)}</span>],
        l.name && ['Event name', l.name],
        ['Listed by', l.organiser?.name ? <>{l.organiser.name}{l.organiser.url && <span className="muted small"> · <span className="mono">{defangDomain(l.organiser.url)}</span></span>}</> : <span className="muted">Not shown</span>],
        l.start && ['Date', fmtDay(l.start)],
        l.place && ['Where', l.place],
        l.price && ['Price', l.price],
      ]} />
      {l.report && <Alert tone="info"><strong>To have it taken down:</strong> {l.report} Say it impersonates your event and isn’t run by you; platforms act fastest on ticket listings.</Alert>}
      <p className="muted small">If this is your own listing, choose <strong>Ours</strong>: other listings by the same organiser are then recognised as yours. Adding your organiser profile link to your channels does the same.</p>
    </Section>
  );
}

/** One lookalike site: our screenshot beside theirs, why we flagged it, and the decision. */
const REPORT_STATUS = {
  queued: ['Sending…', 'neutral'], submitted: ['Sent, waiting for Netcraft', 'neutral'], processing: ['Netcraft is looking at it', 'neutral'],
  malicious: ['Netcraft confirmed it — browsers will warn visitors', 'good'], no_threat: ['Netcraft found nothing wrong (yet)', 'warn'],
  failed: ['Couldn’t send', 'bad'], opened: ['Google’s report form opened', 'neutral'],
};

// Report a confirmed scam site to browser blocklists: usually far faster than a registrar
function Takedown({ takedown, base, onChange }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const send = async (service) => {
    setBusy(service); setError('');
    // Open the window first, in the click: browsers block it after an await
    const win = service === 'google' ? window.open('about:blank', '_blank') : null;
    if (win) win.opener = null;
    try {
      const res = await api(`${base}/takedown`, { method: 'POST', body: { service } });
      if (service === 'google') { if (win) win.location.href = res.open_url; else window.open(res.open_url, '_blank', 'noopener'); }
      onChange();
    } catch (err) { if (win) win.close(); setError(err.message); }
    setBusy('');
  };
  const toggleAuto = async (e) => {
    try { await api(base.replace(/\/\d+$/, '/settings'), { method: 'PUT', body: { auto_takedown: e.target.checked } }); onChange(); } catch (err) { setError(err.message); }
  };
  const active = (service) => takedown.reports.some((r) => r.service === service && ['queued', 'submitted', 'processing', 'malicious'].includes(r.status));
  const netcraftActive = active('netcraft');
  const apwgActive = active('apwg');
  const autoNames = [takedown.netcraft && 'Netcraft', takedown.apwg && 'APWG'].filter(Boolean).join(' and ');
  return (
    <Section title="Report it" description="Browser blocklists put a warning in front of the site for everyone, usually within hours — much faster than a registrar acting.">
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="takedown-actions">
        {takedown.netcraft && <button type="button" className="btn btn-primary btn-small" disabled={!!busy || netcraftActive} onClick={() => send('netcraft')}>{netcraftActive ? 'Reported to Netcraft' : busy === 'netcraft' ? 'Sending…' : 'Report to Netcraft'}</button>}
        {takedown.apwg && <button type="button" className="btn btn-secondary btn-small" disabled={!!busy || apwgActive} onClick={() => send('apwg')}>{apwgActive ? 'Reported to APWG' : busy === 'apwg' ? 'Sending…' : 'Report to APWG'}</button>}
        <button type="button" className="btn btn-secondary btn-small" disabled={!!busy} onClick={() => send('google')}>Report to Google Safe Browsing</button>
      </div>
      <p className="muted small">Netcraft feeds the blocklists in most browsers and email filters{takedown.netcraft ? '' : ' (not set up on this server yet)'}. APWG, the Anti-Phishing Working Group, shares reports with browser, email and security companies{takedown.apwg ? '' : ' (not switched on here yet)'}. Google’s form opens in a new tab, already filled in.</p>
      {autoNames && (
        <label className="takedown-auto small"><input type="checkbox" checked={takedown.auto} onChange={toggleAuto} /> Report every site we confirm as a scam to {autoNames} automatically</label>
      )}
      {takedown.reports.length > 0 && (
        <ul className="takedown-timeline small">
          {takedown.reports.map((r) => (
            <li key={r.id} className={`tone-${REPORT_STATUS[r.status]?.[1] || 'neutral'}`}>
              <strong>{{ netcraft: 'Netcraft', apwg: 'APWG', google: 'Google' }[r.service] || r.service}</strong>: {r.service === 'apwg' && r.status === 'submitted' ? 'Report emailed (APWG doesn’t reply)' : REPORT_STATUS[r.status]?.[0] || r.status}
              <span className="muted"> · {fmtDateTime(r.submitted_at || r.created_at)}{r.automatic ? ' · automatically' : r.by ? ` · ${r.by}` : ''}{r.checked_at ? ` · checked ${fmtDateTime(r.checked_at)}` : ''}</span>
              {r.error && <div className="muted">{r.error}</div>}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// The headline label, in plain words: the company's decision, else what we found
const PLAIN_DECISION = {
  scam: ['Scam site', 'bad'], ours: ['Yours', 'good'], not_related: ['Nothing to do with you', 'neutral'], watch: ['Watching it', 'neutral'],
};
const PLAIN_VERDICT = {
  clone: ['Very likely a scam: a copy of your site', 'bad'], brand_use: ['Uses your name', 'warn'], email_ready: ['Set up to send email', 'warn'],
};
const RESULT = {
  scam: ['Marked as a scam', 'good', ['Anyone who checks it on your check page is told it’s fake.', 'Emails linking to it are caught at every event we protect.', 'We keep checking whether it’s still up.']],
  ours: ['Marked as yours', 'neutral', ['We stop flagging it and just look at it once a month.']],
  watch: ['We’re keeping an eye on it', 'neutral', ['We look at it again every couple of days and tell you if it starts copying you or taking payments.']],
  not_related: ['Marked as nothing to do with you', 'neutral', ['We look at it again in a month, in case that changes.']],
};

export default function LookalikeDetail() {
  const { siteId } = useParams();
  const { organisation } = useAuth();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(location.state?.notice || '');
  const [choice, setChoice] = useState(null);
  const [changing, setChanging] = useState(false);
  const base = organisation ? `/portal/organisations/${organisation.id}/lookalikes/${siteId}` : '';

  const load = useCallback(async () => {
    if (!base) return;
    try { setData(await api(base)); } catch (err) { setError(err.message); }
  }, [base]);
  useEffect(() => { load(); }, [load]);
  // Still checking: look again shortly
  useEffect(() => {
    if (!data || (data.site.verdict !== 'pending' && data.checks.some((c) => c.rendered))) return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [data, load]);

  const back = <Link to="/company/lookalikes" className="back-link">← Fake websites</Link>;
  if (error) return <div className="page"><div className="page-header-top">{back}</div><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;
  const s = data.site;
  const recheck = async () => {
    try { await api(`${base}/recheck`, { method: 'POST' }); setNotice('Checking it again. The picture takes a minute or two.'); setTimeout(load, 5000); } catch (err) { setNotice(err.message); }
  };
  const [label, tone] = s.decision ? PLAIN_DECISION[s.decision] : PLAIN_VERDICT[s.verdict] || [VERDICT[s.verdict]?.label || 'Checking…', 'neutral'];
  const strong = s.reasons.filter((r) => r.points > 0 || STRONG.has(r.code) || r.code === 'claims_to_be_you');
  const other = s.reasons.filter((r) => !strong.includes(r));
  const name = s.listing ? `this ${s.listing.platform} listing` : null;
  const showChoices = data.can_decide && (!s.decision || changing);
  const result = s.decision && RESULT[s.decision];

  return (
    <div className="page">
      <div className="page-header-top">{back}<HowThisPageWorks topic="fake-websites" /></div>
      {notice && <Alert tone="info">{notice}</Alert>}

      <section className="card ld-hero" aria-labelledby="ld-q">
        <div className="ld-tags">
          <span className={`msg-pill tone-${tone}`}>{label}</span>
          <span className="muted">Found {fmtDay(s.created_at)}{s.registered_at ? <> · <RegisteredAgo at={s.registered_at} /></> : ''} · {SOURCE[s.source] || 'Found by us'}</span>
          <span className="spacer" />
          <button type="button" className="btn btn-secondary btn-small" onClick={recheck}>Check again</button>
        </div>
        <h1 id="ld-q" className="ld-question">
          {s.decision && !changing
            ? <span className="ld-domain">{name || defangDomain(s.domain)}</span>
            : name ? <>Is {name} yours?</> : <>Is <span className="ld-domain">{defangDomain(s.domain)}</span> your website?</>}
        </h1>

        <div className="ld-shots">
          <figure>
            {s.screenshot_url
              ? <img src={s.screenshot_url} alt={`Our picture of ${s.domain}`} loading="lazy" />
              : <div className="ld-shot-empty">{s.verdict === 'pending' ? 'Taking a picture of it…' : 'There’s nothing to see on it yet.'}</div>}
            <figcaption>{s.listing ? 'The listing' : 'Their page'}, photographed safely by us</figcaption>
          </figure>
          <figure>
            {data.your_site
              ? <img src={data.your_site.screenshot_url} alt="Our picture of your own site" loading="lazy" />
              : <div className="ld-shot-empty">We don’t have a picture of your site yet.</div>}
            <figcaption>{data.your_site ? <>Your real page, {data.your_site.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}</> : 'Your real page'}</figcaption>
          </figure>
        </div>

        <div className="ld-why">
          <div className="ld-why-main">
            <h2>{s.decision === 'scam' ? 'What we found' : strong.length ? 'Why we think it’s fake' : 'What we found'}</h2>
            {s.reasons.length ? (
              <ul className="ld-reasons">
                {strong.map((r, i) => <li key={`s-${r.code}-${i}`} className="is-strong">{isAiReason(r) && <AiMark />}{r.text}</li>)}
                {other.map((r, i) => <li key={`o-${r.code}-${i}`}>{isAiReason(r) && <AiMark />}{r.text}</li>)}
              </ul>
            ) : <p className="muted">{s.verdict === 'pending' ? 'Still checking it.' : s.verdict === 'no_site' ? 'The domain is registered but there’s no website on it yet. We’ll keep checking: they often go live just before an event.' : s.verdict === 'parked' ? 'A parked or for-sale page. We’ll keep checking in case it goes live.' : 'Nothing on it copies you or uses your name.'}</p>}
          </div>
          <aside className="ld-dontvisit">
            <h2>Don’t visit it yourself</h2>
            <p>Fake sites can try to install things on your computer or take your details. Our pictures are taken in a safe, separate browser, so you don’t need to.</p>
          </aside>
        </div>

        {showChoices && (
          <div className="ld-decide">
            <h2>{changing ? 'Change your answer' : 'What is it?'}</h2>
            <div className={`msg-answers${s.decision ? ' has-current' : ''}`}>
              {CHOICES.map(([k, l, what]) => <AnswerCard key={k} k={k} title={l} what={what} current={s.decision ? { key: s.decision, by: 'you' } : null} onClick={() => setChoice(k)} />)}
            </div>
            {changing && <button type="button" className="btn btn-secondary btn-small" onClick={() => setChanging(false)}>Keep my answer</button>}
          </div>
        )}
        {!data.can_decide && !s.decision && <p className="muted ld-decide">An owner or admin of your company needs to decide what this is.</p>}

        {result && !changing && (
          <div className={`ld-result tone-${result[1]}`}>
            <h2>{result[0]}</h2>
            <ul>{result[2].map((t) => <li key={t}>{t}</li>)}</ul>
            <p className="muted small">{s.decided_by ? `${s.decided_by}, ` : ''}{fmtDateTime(s.decided_at)}{s.decision_note ? ` · “${s.decision_note}”` : ''}</p>
            {data.can_decide && <button type="button" className="btn btn-secondary btn-small" onClick={() => setChanging(true)}>Change your answer</button>}
          </div>
        )}
      </section>

      {data.can_decide && s.decision === 'scam' && data.takedown && <Takedown takedown={data.takedown} base={base} onChange={load} />}

      <details className="ld-tech">
        <summary>Technical details (for your IT person)</summary>
        <div className="ld-tech-body">
          {s.listing ? <ListingFacts site={s} /> : <Registration site={s} organisationName={organisation.name} />}
          <Section title="Details">
            <Facts items={[
              ['Website', <span className="mono">{defangDomain(s.domain)}</span>],
              s.final_url && ['Ended up at', <span className="mono break">{defangDomain(s.final_url)}</span>],
              s.title && ['Page title', s.title],
              ['How we found it', <>{SOURCE[s.source]}{s.looks_like && <span className="muted"> · like {s.looks_like}</span>}</>],
              s.page_url && ['Page found', <span className="mono break">{defangDomain(s.page_url)}</span>],
              s.found_query && ['Searched for', s.found_query],
              s.evidence_url && ['Scan on urlscan.io', <a href={s.evidence_url} target="_blank" rel="noreferrer noopener">View their scan</a>],
              s.ip && ['Hosted at', <span className="mono">{s.ip}</span>],
              s.mail && ['Email', s.mail.canSend
                ? <>{[s.mail.receives && 'Receives email', s.mail.sends && 'allowed to send email'].filter(Boolean).join(', ')}{s.mail.mx?.length ? <span className="muted small"> · <span className="mono">{s.mail.mx.join(', ')}</span></span> : ''}{s.mail.dmarc ? <span className="muted small"> · has a DMARC record</span> : ''}</>
                : <span className="muted">Not set up for email</span>],
              ['Last checked', s.checked_at ? fmtDateTime(s.checked_at) : 'Not yet'],
              ['Next check', s.next_check_at ? fmtDateTime(s.next_check_at) : '—'],
            ]} />
          </Section>
          {data.checks.length > 0 && (
            <Section title="Every check" flush>
              <div className="table-scroll">
                <table className="table table-stack">
                  <thead><tr><th>When</th><th>How</th><th>Found</th><th>Picture</th></tr></thead>
                  <tbody>
                    {data.checks.map((c) => (
                      <tr key={c.id}>
                        <td data-label="When" className="nowrap small">{fmtDateTime(c.checked_at)}</td>
                        <td data-label="How" className="small">{c.rendered ? 'In the browser' : 'Quick fetch'}{c.http_status ? ` · HTTP ${c.http_status}` : ''}</td>
                        <td data-label="Found" className="small">{c.error ? <span className="muted">{c.error}</span> : `${VERDICT[c.verdict]?.label || c.verdict}${c.score ? ` (${c.score})` : ''}`}</td>
                        <td data-label="Picture" className="small">{c.screenshot_url ? <a href={c.screenshot_url} target="_blank" rel="noreferrer">View</a> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
        </div>
      </details>

      {choice && <DecideModal site={s} choice={choice} base={base} onClose={() => setChoice(null)} onDone={() => { setChoice(null); setChanging(false); setNotice('Saved.'); load(); }} />}
    </div>
  );
}
